'use es6';

import { LAYOUT, GLOBAL_WIDGET, GLOBAL_GROUP, CUSTOM_WIDGET, RAW_ASSET, FOLDER, LAYOUT_SOURCE, SETTINGS, WELCOME, TEMPLATE, GETTING_STARTED } from 'DesignData/enums/DesignAssetTypes';
import { JSLINT, CODE, NONE } from 'DesignData/enums/settings/Categories';
import { MYSQL } from 'DesignData/enums/AssetSources';
import { LAYOUT as FOLDER_ASSET_TYPE_LAYOUT, TEMPLATE as FOLDER_ASSET_TYPE_TEMPLATE, CUSTOM_WIDGET as FOLDER_ASSET_TYPE_CUSTOM_WIDGET, GLOBAL_WIDGET as FOLDER_ASSET_TYPE_GLOBAL_WIDGET, RAW_ASSET as FOLDER_ASSET_TYPE_RAW_ASSET } from 'DesignData/constants/FolderAssetTypes';
import EditorOptions from 'DesignData/models/app/EditorOptions';
import { trimSlashesAndWhitespace } from 'DesignData/lib/text';
import { TEMPLATE_REVISION_FETCH, TEMPLATE_BUFFER_FETCH, TEMPLATE_BUFFER_FETCH_BY_PATH, UNIFIED_LAYOUT_FETCH, LAYOUT_REVISION_FETCH, GLOBAL_WIDGET_FETCH, GLOBAL_WIDGET_RENAME, CUSTOM_WIDGET_BUFFER_FETCH, CUSTOM_WIDGET_BUFFER_FETCH_BY_PATH, CUSTOM_WIDGET_REVISION_FETCH, RAW_ASSET_BUFFER_FETCH, RAW_ASSET_BUFFER_FETCH_BY_PATH } from 'DesignData/actions/ActionTypes';
import TemplateModel from 'DesignData/models/Template';
import CustomWidgetModel from 'DesignData/models/CustomWidget';
import LayoutModel from 'DesignData/models/layouts/Layout';
import GlobalWidgetModel from 'DesignData/models/GlobalWidget';
import FileModel from 'DesignData/models/finder/File';
import RawAssetModel from 'DesignData/models/RawAsset'; // TODO: Start leaning away from createPath()
// TODO: Unify code in following path functions.

/**
 *
 * @param {DesignAssetTypes}  assetType
 * @param {Number|String}     assetId
 * @param {EditorOptions}     editorOptions
 * @return {String}
 */

export const createPath = (assetType, assetId, editorOptions) => {
  editorOptions = editorOptions || EditorOptions.EMPTY;
  const {
    assetSource
  } = editorOptions;
  const usePath = assetSource && assetSource !== MYSQL;

  switch (assetType) {
    case LAYOUT:
    case GLOBAL_GROUP:
      return `/templates/${assetId}`;

    case TEMPLATE:
      return usePath ? `/code/p/${trimSlashesAndWhitespace(assetId)}` : `/code/${assetId}`;

    case CUSTOM_WIDGET:
      return usePath ? `/modules/p/${trimSlashesAndWhitespace(assetId)}` : `/modules/${assetId}`;

    case GLOBAL_WIDGET:
      return `/global-modules/${assetId}`;

    case RAW_ASSET:
      return usePath ? `/file/p/${trimSlashesAndWhitespace(assetId)}` : `/file/${assetId}`;

    case FOLDER:
      return `/folder/${assetId}`;

    case LAYOUT_SOURCE:
      return `/templates/${assetId}/source`;

    case SETTINGS:
      {
        switch (editorOptions.settingsCategory) {
          case JSLINT:
            return '/settings/javascript-linter';

          case CODE:
            return '/settings/code';

          case NONE:
          default:
            return '/settings';
        }
      }

    case WELCOME:
      return `/welcome`;

    case GETTING_STARTED:
      return '/getting-started';

    default:
      return '/';
  }
}; // TODO: Where possible replace occurrences of createPath with this - more straightforward.
// TODO: Unify string creation

export const createPathFromAsset = asset => {
  const usePath = asset.id && typeof asset.id === 'string' || asset.assetSource && asset.assetSource !== MYSQL;

  if (asset instanceof TemplateModel) {
    return usePath ? `/code/p/${trimSlashesAndWhitespace(asset.path)}` : `/code/${asset.id}`;
  }

  if (asset instanceof CustomWidgetModel) {
    return usePath ? `/modules/p/${trimSlashesAndWhitespace(asset.path)}` : `/modules/${asset.id}`;
  }

  if (asset instanceof RawAssetModel) {
    return usePath ? `/file/p/${trimSlashesAndWhitespace(asset.path)}` : `/file/${asset.id}`;
  }

  if (asset instanceof LayoutModel) {
    return `/templates/${asset.id}`;
  }

  if (asset instanceof GlobalWidgetModel) {
    return `/global-modules/${asset.id}`;
  }

  return null;
};
export const createPathFromFile = file => {
  if (!(file instanceof FileModel)) {
    return null;
  }

  const {
    assetSource,
    id = null,
    path = null
  } = file;
  const usePath = assetSource && assetSource !== MYSQL;

  switch (file.designAssetType) {
    case LAYOUT:
    case GLOBAL_GROUP:
      return id && `/templates/${id}`;

    case TEMPLATE:
      return usePath ? path && `/code/p/${trimSlashesAndWhitespace(path)}` : id && `/code/${id}`;

    case CUSTOM_WIDGET:
      return usePath ? path && `/modules/p/${trimSlashesAndWhitespace(path)}` : id && `/modules/${id}`;

    case GLOBAL_WIDGET:
      return id && `/global-modules/${id}`;

    case RAW_ASSET:
      return usePath ? path && `/file/p/${trimSlashesAndWhitespace(path)}` : id && `/file/${id}`;

    case FOLDER:
      return id && `/folder/${id}`;

    default:
      return null;
  }
};
export function createRevisionPath(assetType, assetId, revisionId) {
  const assetPath = createPath(assetType, assetId);
  if (assetPath === '/') return assetPath;
  const revisionsPath = `${assetPath}/revisions`;
  return revisionId ? `${revisionsPath}/${revisionId}` : revisionsPath;
}
export function createDependentsPath(assetType, assetId) {
  const assetPath = createPath(assetType, assetId);
  if (assetPath === '/') return assetPath;
  return `${assetPath}/dependents`;
}
export function create404Path(actionType) {
  switch (actionType) {
    case UNIFIED_LAYOUT_FETCH:
    case LAYOUT_REVISION_FETCH:
      return `/not-found/${FOLDER_ASSET_TYPE_LAYOUT}`;

    case TEMPLATE_REVISION_FETCH:
    case TEMPLATE_BUFFER_FETCH:
    case TEMPLATE_BUFFER_FETCH_BY_PATH:
      return `/not-found/${FOLDER_ASSET_TYPE_TEMPLATE}`;

    case CUSTOM_WIDGET_BUFFER_FETCH:
    case CUSTOM_WIDGET_BUFFER_FETCH_BY_PATH:
    case CUSTOM_WIDGET_REVISION_FETCH:
      return `/not-found/${FOLDER_ASSET_TYPE_CUSTOM_WIDGET}`;

    case GLOBAL_WIDGET_FETCH:
    case GLOBAL_WIDGET_RENAME:
      return `/not-found/${FOLDER_ASSET_TYPE_GLOBAL_WIDGET}`;

    case RAW_ASSET_BUFFER_FETCH:
    case RAW_ASSET_BUFFER_FETCH_BY_PATH:
      return `/not-found/${FOLDER_ASSET_TYPE_RAW_ASSET}`;

    default:
      return '/not-found';
  }
}
export function createLayoutSourcePath(assetId) {
  const assetPath = createPath(LAYOUT, assetId);
  return `${assetPath}/source`;
}